/* eslint-disable react-hooks/exhaustive-deps */
import { cloneDeep, get, omit, set } from "lodash";
import { createContext, useContext, useEffect, useState } from "react";
import {
  TruAction,
  ErrorSchema,
  TruInvokeActionData,
  TruInvokeAction,
  TruUpdateActionData,
  FieldChange,
  TruChangeStatusAction,
  TruToast,
  ListItemsBody,
  InvokeActionBody,
  TruMutateValidateAction,
} from "../../types";
import { setInstancePathFormat } from "../../utils/DynamicForms";
import { ScrollSpyContextProvider } from "./ScrollSpyContext";
import { useValidation } from "../../hooks/useValidation";
import { JsonSchema } from "@jsonforms/core";
import { ajv } from "@trufla-technology/trufla-truvalidate";
import { message } from "antd";

type Props = {
  data?: any;
  schema?: JsonSchema;
  uiSchema?: JsonSchema;
  children: React.ReactNode;
  isActionsPending: boolean[];
  onDataUpdate?: (data: any) => void;
  onBlur?: (data: any) => Promise<void>;
  onMutate?: (data?: any) => Promise<void>;
  onErrorsUpdate?: (errors: ErrorSchema[], valid: boolean) => void;
  onNavigate?: (
    payload: { search?: string; pathname?: string },
    reload?: boolean
  ) => void;
  onNavigateWithCondition?: (
    payload: {
      search?: string;
      pathname?: string;
      redirectBackParams?: { stepNum: number };
    },
    condition: { path: string },
    options?: { reload: boolean; redirect: boolean }
  ) => void;
  onInvokeAction?: (
    actionId: string,
    data?: TruInvokeActionData,
    loading?: boolean
  ) => Promise<void>;
  onChangeUiSchema?: (
    schemaId: string,
    setData?: boolean,
    fetch?: boolean
  ) => Promise<void>;
  onGetAllDocs?: (data:string[]) => Promise<void>;
  onFetchUiSchema?: (schemaId: string) => Promise<void>;
  onUpdate?: (data: TruUpdateActionData) => Promise<void>;
  onChangeStatus?: (data: TruChangeStatusAction["data"]) => Promise<void>;
  onActionError?: (message: TruAction["key"], data?: any) => void;
  onToast?: (toast: TruToast) => void;
  useListItems?: (payload: ListItemsBody) => any;
  onPartialDataUpdate?: (data: any) => Promise<void>;
  onActionsPending?: (data: any) => void;
  useInvokeAction?: () => (payload: InvokeActionBody) => Promise<null>;
  onFetchDocumentData?: () => (payload: InvokeActionBody) => Promise<null>;
  onPhoneUpdate?: (data: any, actionData:any) => void;
  onUpdateClient?: (data: any, actionData: any) => void;
  onStartLoading?: () => void;
};

type Context = {
  formData: any;
  errors: ErrorSchema[];
  schema: JsonSchema;
  uiSchema: JsonSchema | undefined;
  isActionsPending: any;
  handleFieldChange: (path: string, value: any) => void;
  handleMutateChanges: (changes: FieldChange[]) => void;
  handleFieldRemove: (path: string) => void;
  handleActions: (actions: TruAction[]) => Promise<void>;
  handleBlur: (path: string, e?: any) => void;
  useListItems?: (payload: ListItemsBody) => any;
  useInvokeAction?: () => (payload: InvokeActionBody) => Promise<null>;
  setData?: (data: any) => void;
  valid?: boolean;
};

const DynamicFormContext = createContext<Context | null>(null);

export const DynamicFormContextProvider = ({
  data = {},
  schema = {},
  uiSchema,
  isActionsPending,
  children,
  onDataUpdate,
  onErrorsUpdate,
  onInvokeAction,
  onNavigate,
  onNavigateWithCondition,
  onUpdate,
  onChangeUiSchema,
  onFetchUiSchema,
  onChangeStatus,
  onBlur,
  onMutate,
  onActionError,
  onToast,
  useListItems,
  onPartialDataUpdate,
  onActionsPending,
  onFetchDocumentData,
  useInvokeAction,
  onPhoneUpdate,
  onUpdateClient,
  onStartLoading,
}: Props) => {
  const [formData, setFormData] = useState(data);
  const { errors, validate, valid, resetErrors } = useValidation(schema, ajv, uiSchema);
  useEffect(() => {
    setFormData(data);
  }, [data.stepNum]);

  useEffect(() => {
    setFormData(data);
  }, [data.quoteHasPremium]);

  const handleFieldChange = (path: string, value: any) => {
    setFormData((currentData: any) => {
      const updatedData = set(cloneDeep(currentData), path, value);
      validate({
        data: cloneDeep(updatedData),
        prop: "instancePath",
        value: setInstancePathFormat(path),
      });
      return updatedData;
    });
  };

  const setData = (data: any) => {
    setFormData(data);
    onBlur?.(data);
  };

  const handleMutateChanges = (changes: FieldChange[]) => {
    setFormData((currentData: any) => {
      let updatedData = cloneDeep(currentData);

      const fieldsToRemove = changes.filter(
        (change) => change.value === undefined
      );

      const fieldsToEdit = changes.filter(
        (change) => change.value !== undefined
      );

      fieldsToEdit.forEach((change) => {
        set(updatedData, change.path, change.value);
      });

      fieldsToRemove.forEach((change) => {
        updatedData = omit(updatedData, [change.path]);
      });

      onBlur?.(updatedData);
      onMutate?.(updatedData);

      return updatedData;
    });
  };

  const handleValidate = (
    mutatedData: any,
    action: TruMutateValidateAction
  ) => {
    let validationResult: any;
    if (action?.options) {
      const subSchema = get(schema, action?.options?.schemaScope!);
      const subData = get(mutatedData, action?.options?.dataPath!);
      validationResult = validate(
        {
          data: cloneDeep(subData),
        },
        subSchema,
        `/${action?.options?.dataPath!.replace(".", "/")}`
      );
    } else {
      validationResult = validate({
        data: cloneDeep(mutatedData),
      });
    }
    if (!validationResult?.valid) {
      onActionError?.(action.key, {
        message: "Error validating form!",
        errors: validationResult?.errors,
      });
      return;
    }
    return validationResult?.valid;
  };

  const getUpdatedData = (changes: FieldChange[]) => {
    let updatedData = cloneDeep(formData);

    const fieldsToRemove = changes.filter(
      (change) => change.value === undefined
    );

    const fieldsToEdit = changes.filter((change) => change.value !== undefined);

    fieldsToEdit.forEach((change) => {
      set(updatedData, change.path, change.value);
    });

    fieldsToRemove.forEach((change) => {
      updatedData = omit(updatedData, [change.path]);
    });

    return updatedData;
  };

  const handleMutateValidate = (
    changes: FieldChange[],
    action: TruMutateValidateAction
  ) => {
    let updatedData = getUpdatedData(changes);

    const isValid = handleValidate(updatedData, action);

    if (isValid) {
      onBlur?.(updatedData);
      onMutate?.(updatedData);
      setFormData(updatedData);
      return true;
    } else return false;
  };

  const handleMutatePartialUpdate = async (changes: FieldChange[]) => {
      let updatedData = getUpdatedData(changes);
      onActionsPending?.({ partialUpdate: true });
      const res = await onPartialDataUpdate?.(updatedData);
      if(res){
        onBlur?.(updatedData);
        onMutate?.(updatedData);
        setFormData(updatedData);
      }else{
        message.error("Something went wrong , please try again");
        onActionsPending?.({ partialUpdate: false });
        return false
      }
      onActionsPending?.({ partialUpdate: false });
      return true
  };

  const handleFieldRemove = (path: string) => {
    setFormData((currentData: any) => {
      const updatedData = omit(cloneDeep(currentData), [path]);
      validate({
        data: cloneDeep(updatedData),
        prop: "instancePath",
        value: setInstancePathFormat(path),
      });
      return updatedData;
    });
  };

  const handleActions = async (actions: TruAction[]) => {
    for (const action of actions) {
      try {
        switch (action.key) {
          case "validate":
            let validationResult: any;
            if (action?.options) {
              const subSchema = get(schema, action?.options?.schemaScope!);
              const subData = get(formData, action?.options?.dataPath!);
              validationResult = validate(
                {
                  data: cloneDeep(subData),
                },
                subSchema,
                `/${action?.options?.dataPath!.replace(".", "/")}`
              );
            } else {
              validationResult = validate({
                data: cloneDeep(formData),
              });
            }
            if (!validationResult?.valid) {
              onActionError?.(action.key, {
                message: "Error validating form!",
                errors: validationResult?.errors,
              });
              return;
            }
            break;
          case "sendFormUpdates":
            await onBlur?.(formData);
            break;
          case "invoke":
            await onInvokeAction?.(
              action.actionId,
              (action as TruInvokeAction).data,
              action.loading
            );
            break;
          case "mutate":
            handleMutateChanges(action.data);
            break;
          case "mutateAndValidate":
            const isValid = handleMutateValidate(action.data, action);
            if (!isValid) return;
            break;
          case "partialDataUpdate":
            await onPartialDataUpdate?.(formData);
            break;
          case "mutateAndPartialUpdate":
            const isSuccess = await handleMutatePartialUpdate(action.data);
            if (!isSuccess) return;
            break;
          case 'updatePhone':
            await onPhoneUpdate?.(formData, action.data);
            break;
          case 'updateClient':
            await onUpdateClient?.(formData, action.data);
            break;
          case "fetchDocumentData":
            await onFetchDocumentData?.();
            break;
          case "navigate":
            onNavigate?.(action.data, action.reload);
            break;
          case "navigateWithCondition":
            onNavigateWithCondition?.(
              action.data,
              action.condition,
              action.options
            );
            break;
          case "update":
            await onUpdate?.(action.data);
            break;
          case "changeUiSchema":
            await onChangeUiSchema?.(
              action.data.uiSchemaKey,
              action.setData,
              action.fetch
            );
            break;
          case "fetchUiSchema":
            await onFetchUiSchema?.(action.data.uiSchemaKey);
            break;
          case "changeStatus":
            await onChangeStatus?.(action.data);
            break;
          case "removePaths":
            action.data.paths.forEach((removedPath: string) => {
              handleFieldRemove(removedPath);
            });
            break;
          case "toast":
            await onToast?.(action.toast);
            break;
          case "startLoading":
            onStartLoading?.();
            break;
          case "resetErrors":
            await resetErrors();
        }
      } catch (err) {
        //TODO : roll back any changes
        onActionError?.(action.key, err);
        console.log(err);
      }
    }
  };

  const handleBlur = (path: string) => {
    onBlur?.(formData);
    validate({
      data: cloneDeep(formData),
      prop: "instancePath",
      value: setInstancePathFormat(path),
    });
  };

  useEffect(() => {
    onDataUpdate?.(formData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  useEffect(() => {
    onErrorsUpdate?.(errors, valid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  return (
    <DynamicFormContext.Provider
      value={{
        formData,
        errors,
        schema,
        uiSchema,
        isActionsPending,
        handleFieldChange,
        handleFieldRemove,
        handleActions,
        handleBlur,
        handleMutateChanges,
        useListItems,
        useInvokeAction,
        setData,
        valid,
      }}
    >
      <ScrollSpyContextProvider>{children}</ScrollSpyContextProvider>
    </DynamicFormContext.Provider>
  );
};

export const useDynamicFormContext = () => {
  const context = useContext(DynamicFormContext);

  if (!context)
    throw new Error(
      "DynamicFormContext must be called from within the DynamicFormContextProvider"
    );

  return context;
};
